import {
  pluralizeDeviceNoun,
  tDeploymentScope,
} from "@/i18n/deploymentScopeStrings";

/**
 * Formats the right-hand-side text of the wizard's "Deployment scope" row.
 *
 * Rules:
 * - Catalog target → "Follows the app".
 * - External target, no tags → "Select one or more tags below".
 * - External, 1–3 tags → comma-joined tag names (sorted alphabetically).
 * - External, 4+ tags  → "N tags selected".
 *
 * When at least one tag is selected and a non-null `deviceCount` is known,
 * we append a device suffix:
 * - Single tag → "· N devices".
 * - Multiple tags → "· up to N devices" (because the API does not union
 *   memberships across tags, the sum is an upper bound).
 *
 * Loading / error states pass `deviceCount: undefined` and the suffix is
 * omitted — never block the row text on the count.
 *
 * ## i18n
 *
 * All user-facing literals are routed through `tDeploymentScope` (see
 * `src/i18n/deploymentScopeStrings.ts`). The default English bundle ships
 * with the function so existing callers don't need to change. Bootstrap a
 * locale once via `configureDeploymentScopeStrings({...})` and every render
 * picks it up.
 *
 * The structural separator (` · `) and the comma-join glue (`", "`) are
 * intentionally NOT in the registry: they're typographic punctuation, not
 * translatable copy. If a future locale needs different separators, lift
 * them into the registry as `nameSeparator` / `headSuffixSeparator`.
 */
export function formatDeploymentScope(args: {
  /** True for external targets that require explicit tag selection. */
  tagsRequired: boolean;
  /** Names of currently selected tags, in stable display order. */
  selectedTagNames: string[];
  /** Aggregated device count for the selected tags. Undefined while loading. */
  deviceCount?: number;
  /** True when `deviceCount` represents a single tag (and is therefore exact). */
  exact?: boolean;
}): string {
  const { tagsRequired, selectedTagNames, deviceCount, exact } = args;

  if (!tagsRequired) return tDeploymentScope("followsApp");
  if (selectedTagNames.length === 0) return tDeploymentScope("selectTagsPrompt");

  // Sort case-insensitively (locale-aware) so the row text is stable
  // regardless of the order the user clicked tags or the API returned them.
  // We copy first — never mutate the caller's array.
  const sortedNames = [...selectedTagNames].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );

  const head =
    sortedNames.length <= 3
      ? sortedNames.join(", ")
      : tDeploymentScope("tagsSelectedSummary", { count: sortedNames.length });

  if (typeof deviceCount !== "number") return head;

  // Locale-aware: delegates to Intl.PluralRules so languages with richer
  // grammar (ru, ar, pl, cy…) pick the correct CLDR category instead of the
  // naïve `count === 1 ? singular : plural` split.
  const noun = pluralizeDeviceNoun(deviceCount);
  const suffix = exact
    ? tDeploymentScope("exactDeviceSuffix", { count: deviceCount, noun })
    : tDeploymentScope("upToDeviceSuffix", { count: deviceCount, noun });
  return `${head} · ${suffix}`;
}
